import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import { FaEdit, FaTrash, FaPlusCircle, FaFileExcel, FaEye } from 'react-icons/fa'
import { getDocument, downloadDocument } from '../../data/fileData'
import * as XLSX from 'xlsx'
import { SiteReport, CreateSiteReport, UpdateSiteReport } from '../../data/interfaces'
import {
  getSiteReportsByJob,
  createSiteReport,
  updateSiteReport,
  deleteSiteReport,
} from '../../data/siteReportData'
import SiteReportForm from '../FormComps/SiteReports/SiteReportForm'
import { useToast } from '../../contexts/ToastContext'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'

interface SiteReportsTabProps {
  jobId: number
  lockEdit: boolean
  // When the parent renders its own actions (e.g. in a section header) it can
  // hide the built-in action row and drive these via the ref.
  hideActions?: boolean
}

export interface SiteReportsTabHandle {
  addReport: () => void
  exportToExcel: () => void
  canExport: boolean
}

const SiteReportsTab = forwardRef<SiteReportsTabHandle, SiteReportsTabProps>((props, ref) => {
  const { showToast } = useToast()
  const [reports, setReports] = useState<SiteReport[]>([])
  const [loading, setLoading] = useState(false)
  const [formVisible, setFormVisible] = useState(false)
  const [selectedReport, setSelectedReport] = useState<SiteReport | null>(null)
  const [viewReport, setViewReport] = useState<SiteReport | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  const loadReports = useCallback(async () => {
    if (!props.jobId) return
    setLoading(true)
    try {
      const data = await getSiteReportsByJob(props.jobId)
      setReports(data)
    } catch {
      showToast('Failed to load site reports', 'error')
    } finally {
      setLoading(false)
    }
  }, [props.jobId])

  useEffect(() => {
    loadReports()
  }, [loadReports])

  const handleAdd = () => {
    setSelectedReport(null)
    setFormVisible(true)
  }

  const handleEdit = (report: SiteReport) => {
    setSelectedReport(report)
    setFormVisible(true)
  }

  const handleView = (report: SiteReport) => {
    setViewReport(report)
  }

  const handleFormSubmit = async (data: CreateSiteReport | UpdateSiteReport) => {
    try {
      if ('siteReportId' in data) {
        await updateSiteReport(data.siteReportId, data as UpdateSiteReport)
        showToast('Site report updated', 'success')
      } else {
        await createSiteReport(data as CreateSiteReport)
        showToast('Site report created', 'success')
      }
      setFormVisible(false)
      setSelectedReport(null)
      await loadReports()
    } catch {
      showToast('Failed to save site report', 'error')
    }
  }

  const handleFormCancel = () => {
    setFormVisible(false)
    setSelectedReport(null)
  }

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId == null) return
    try {
      await deleteSiteReport(deleteConfirmId)
      showToast('Site report deleted', 'success')
      setDeleteConfirmId(null)
      await loadReports()
    } catch {
      showToast('Failed to delete site report', 'error')
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const truncate = (str: string | null, len = 80) => {
    if (!str) return '—'
    return str.length > len ? str.substring(0, len) + '…' : str
  }

  const handleExportToExcel = () => {
    if (reports.length === 0) {
      showToast('No site reports to export', 'error')
      return
    }

    const rows = reports.map((r) => ({
      Date: formatDate(r.reportDate),
      'Weather & Impact to Works': r.weatherAndImpact ?? '',
      'Plant & Equipment on Site': r.plantAndEquipment ?? '',
      'Work Activities Completed Today': r.workActivities ?? '',
      'Delays / Issues / Constraints': r.delaysIssuesConstraints ?? '',
      'Instructions / Verbal Discussions': r.instructionsVerbalDiscussions ?? '',
      'Dayworks Carried Out – Labour / Material': r.dayworksLabourMaterial ?? '',
      'Created By': r.createdBy ?? '',
      'Created Date': formatDate(r.createdDate),
      'Attachments': r.attachments.map((a) => a.fileName).join(', '),
    }))

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)

    // Auto-column widths
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String((r as any)[key]).length)) + 2,
    }))
    ws['!cols'] = colWidths

    XLSX.utils.book_append_sheet(wb, ws, 'Site Reports')
    XLSX.writeFile(wb, `site_reports_job${props.jobId}_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  useImperativeHandle(ref, () => ({
    addReport: handleAdd,
    exportToExcel: handleExportToExcel,
    canExport: reports.length > 0,
  }), [reports])

  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      {/* Header row */}
      {!props.hideActions && (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px', gap: '10px' }}>
        {!props.lockEdit && (
          <button
            onClick={handleAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: 'rgb(174, 199, 65)',
              color: 'rgb(39, 46, 10)',
              border: 'none',
              borderRadius: '4px',
              padding: '0 16px',
              height: '32px',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            <FaPlusCircle /> New Site Report
          </button>
        )}
        <button
          onClick={handleExportToExcel}
          disabled={reports.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: reports.length === 0 ? '#a5c8a5' : '#1e6b30',
            color: '#ffffff',
            border: 'none',
            borderRadius: '4px',
            padding: '0 16px',
            height: '32px',
            fontSize: '14px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: reports.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <FaFileExcel /> Export to Excel
        </button>
      </div>
      )}

      {/* List */}
      {loading ? (
        <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>Loading...</p>
      ) : reports.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
          No site reports for this job yet.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="grid__table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '110px' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Weather &amp; Impact</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Work Activities</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '110px' }}>Created By</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '90px' }}>Attachments</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd', width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr
                  key={report.siteReportId}
                  style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                >
                  <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{formatDate(report.reportDate)}</td>
                  <td style={{ padding: '12px', color: '#444' }}>{truncate(report.weatherAndImpact)}</td>
                  <td style={{ padding: '12px', color: '#444' }}>{truncate(report.workActivities)}</td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>{report.createdBy ?? '—'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {report.attachments.length > 0 ? (
                      <span style={{ fontWeight: 600, color: '#1e2a4e' }}>{report.attachments.length}</span>
                    ) : (
                      <span style={{ color: '#aaa' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                      <FaEye
                        style={{ cursor: 'pointer', color: '#555', fontSize: '16px' }}
                        title="View"
                        onClick={() => handleView(report)}
                      />
                      {!props.lockEdit && (
                        <>
                          <FaEdit
                            style={{ cursor: 'pointer', color: '#1e2a4e', fontSize: '16px' }}
                            title="Edit"
                            onClick={() => handleEdit(report)}
                          />
                          <FaTrash
                            style={{ cursor: 'pointer', color: '#e9530d', fontSize: '16px' }}
                            title="Delete"
                            onClick={() => setDeleteConfirmId(report.siteReportId)}
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit dialog */}
      {formVisible && (
        <SiteReportForm
          report={selectedReport}
          jobId={props.jobId}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          onAttachmentUploaded={loadReports}
        />
      )}

      {/* View-only dialog */}
      {viewReport && (
        <Dialog
          title={`Site Report — ${formatDate(viewReport.reportDate)}`}
          onClose={() => setViewReport(null)}
          appendTo={document.body}
          width={700}
          style={{ maxHeight: '90vh' }}
        >
          <div style={{ padding: '16px 8px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
            <ViewField label="Date" value={formatDate(viewReport.reportDate)} />
            <ViewField label="Weather &amp; Impact to Works" value={viewReport.weatherAndImpact} />
            <ViewField label="Plant &amp; Equipment on Site" value={viewReport.plantAndEquipment} />
            <ViewField label="Work Activities Completed Today" value={viewReport.workActivities} />
            <ViewField label="Delays / Issues / Constraints" value={viewReport.delaysIssuesConstraints} />
            <ViewField label="Instructions / Verbal Discussions" value={viewReport.instructionsVerbalDiscussions} />
            <ViewField label="Dayworks Carried Out – Labour / Material" value={viewReport.dayworksLabourMaterial} />
            {viewReport.attachments.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <strong>Attachments:</strong>
                <div style={{ marginTop: '6px' }}>
                  {viewReport.attachments.map((a) => (
                    <div key={a.documentId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                      <span>📎</span>
                      <button
                        type="button"
                        onClick={() => getDocument(a.documentId, a.fileName)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#1e2a4e',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: '14px',
                        }}
                      >
                        {a.fileName}
                      </button>
                      <button
                        type="button"
                        title="Download file"
                        onClick={() => downloadDocument(a.documentId, a.fileName)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0 4px',
                          color: '#1e2a4e',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      >
                        ⬇
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p style={{ fontSize: '13px', color: '#888', marginTop: '16px' }}>
              Created by {viewReport.createdBy ?? 'Unknown'} on {formatDate(viewReport.createdDate)}
            </p>
          </div>
          <DialogActionsBar>
            {!props.lockEdit && (
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                onClick={() => {
                  setViewReport(null)
                  handleEdit(viewReport)
                }}
              >
                Edit
              </button>
            )}
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={() => setViewReport(null)}
            >
              Close
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Delete confirmation */}
      {deleteConfirmId != null && (
        <Dialog
          title="Confirm Delete"
          onClose={() => setDeleteConfirmId(null)}
          appendTo={document.body}
          width={380}
        >
          <p style={{ margin: '20px', textAlign: 'center' }}>
            Are you sure you want to delete this site report? This cannot be undone.
          </p>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-error"
              onClick={handleDeleteConfirm}
            >
              Delete
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
})

interface ViewFieldProps {
  label: string
  value: string | null | undefined
}

const ViewField = ({ label, value }: ViewFieldProps) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ fontWeight: 600, fontSize: '13px', color: '#555', marginBottom: '4px' }}
      dangerouslySetInnerHTML={{ __html: label }}
    />
    <div style={{
      padding: '8px 10px',
      background: '#f9f9f9',
      border: '1px solid #e8e8e8',
      borderRadius: '4px',
      fontSize: '14px',
      whiteSpace: 'pre-wrap',
      minHeight: '36px',
      color: value ? '#222' : '#aaa',
    }}>
      {value || '—'}
    </div>
  </div>
)

export default SiteReportsTab
